import {
  Alert,
  Autocomplete,
  Box,
  Checkbox,
  Collapse,
  FormControl,
  FormControlLabel,
  FormHelperText,
  FormLabel,
  LinearProgress,
  List,
  ListItem,
  ListItemText,
  ListSubheader,
  Radio,
  RadioGroup,
  TextField,
  Typography,
  useTheme,
} from "@mui/material";
import { ResourceDWR } from "../../dto/ResourceDWR";
import { HoldingsInformation } from "../../dto/HoldingsInformation";
import { useLanguage } from "../Language";
import { useLocationConfiguration } from "../LocationConfiguration";
import React, {
  useEffect,
  useImperativeHandle,
  useMemo,
  useState,
} from "react";
import {
  Control,
  Controller,
  useController,
  useForm,
  useWatch,
} from "react-hook-form";
import { useItemAvailability } from "../pages/resource/ItemAvailability";
import { useIsMobile } from "@tlc/ui/useIsMobile";
import { InterfaceType, PlaceHoldPayloadItem } from "../services/Holds";
import { useBranchLimits } from "../BranchLimits";
import {
  CopyPrimaryDisplay,
  CopyStatusDisplay,
  serialSort,
} from "../pages/resource/Holdings";
import { useSingleBranch } from "./useSingleBranch";
import { useOnShelfError } from "./useOnShelfError";
import { SelectedLocationsCopiesToggle } from "../SelectedLocationsCopiesToggle";
import { FormDialog } from "@tlc/ui/dialog/FormDialog";
import { FocusableInputHandle } from "@tlc/forms/CheckboxGroup";
interface PlaceHoldCopiesProps {
  pickupLocationId: string | null;
  resource: ResourceDWR;
  control: Control<PlaceHoldFormInput>; // TODO use FormProvider and useFormContext instead
}
export const PlaceHoldCopies = React.forwardRef<
  FocusableInputHandle,
  PlaceHoldCopiesProps
>(function PlaceHoldCopies(
  { resource, control, pickupLocationId }: PlaceHoldCopiesProps,
  ref: React.Ref<FocusableInputHandle>,
) {
  // TODO abstract common logic with CheckboxGroup
  const { itemAvailability, ready: itemAvailabilityReady } =
    useItemAvailability(resource.holdingsInformations, resource);
  const { translate } = useLanguage();
  const { locationConfiguration } = useLocationConfiguration();
  const { allowOnShelfHolds } =
    locationConfiguration?.pacSettings?.userActionSettings || {};

  const { displayInCopiesBranchIds, selectedBranchIds } = useBranchLimits();
  const [viewSelected, setViewSelected] = useState<boolean>(true);

  // TODO update useVisibleHoldings to accept viewSelected and includeTextualHoldings
  const visibleHoldings = useMemo(() => {
    return resource.holdingsInformations.filter(({ branchIdentifier }) =>
      viewSelected && selectedBranchIds?.length
        ? selectedBranchIds.includes(branchIdentifier)
        : displayInCopiesBranchIds.includes(branchIdentifier),
    );
  }, [viewSelected, displayInCopiesBranchIds, selectedBranchIds]);

  const holdings = useMemo(() => {
    if (allowOnShelfHolds) {
      return visibleHoldings;
    }

    if (!itemAvailabilityReady) {
      return null;
    }

    return visibleHoldings.filter(
      (hi) =>
        hi.branchIdentifier !== pickupLocationId ||
        !itemAvailability.get(hi.barcode)?.available,
    );
  }, [
    visibleHoldings,
    itemAvailability,
    itemAvailabilityReady,
    pickupLocationId,
  ]);

  const locationHoldingsMap = useMemo(() => {
    const groupedHoldings = new Map<string, HoldingsInformation[]>();
    if (!holdings?.length) {
      return groupedHoldings;
    }
    for (const h of holdings) {
      if (!groupedHoldings.has(h.branchName)) {
        groupedHoldings.set(h.branchName, [h]);
      } else {
        groupedHoldings.get(h.branchName).push(h);
      }
    }

    if (resource.serial) {
      for (const arr of groupedHoldings.values()) {
        arr.sort(serialSort);
      }
    }
    return groupedHoldings;
  }, [holdings, resource.serial]);

  const displayHoldingsFilteredAlert = useMemo(() => {
    return !!holdings && visibleHoldings.length !== holdings.length;
  }, [visibleHoldings, holdings]);

  const {
    field: { onChange, value, ...field },
    fieldState: { error },
  } = useController({
    name: "barcodes",
    control,
    rules: {
      required: translate("isRequired", {
        LABEL: translate("copies"),
      }),
    },
  });

  const firstCheckboxRef = React.useRef<HTMLInputElement>(null);
  const setFirstCheckboxRef = (node: HTMLInputElement | null) => {
    field.ref(node); // for RHF registration
    firstCheckboxRef.current = node; // for external focus
  };
  useImperativeHandle(
    ref,
    () => ({
      focus: () => firstCheckboxRef.current?.focus(),
    }),
    [],
  );
  const theme = useTheme();

  return (
    <FormControl
      component="fieldset"
      variant="standard"
      error={!!error}
      required
    >
      <FormLabel component="legend">{translate("copies")}</FormLabel>
      <FormHelperText id="place-hold-copies-helper-text">
        {error?.message || " "}
      </FormHelperText>
      <SelectedLocationsCopiesToggle
        resource={resource}
        viewSelected={viewSelected}
        onChange={(viewSelected) => setViewSelected(viewSelected)}
      ></SelectedLocationsCopiesToggle>
      {displayHoldingsFilteredAlert && (
        <Alert variant={"outlined"} severity={"info"}>
          {translate("OnShelfInfoChooseFromTheseItems")}
        </Alert>
      )}
      <List
        disablePadding
        component={"div"}
        sx={{
          padding: 0,
        }}
      >
        {!itemAvailabilityReady ? (
          <LinearProgress />
        ) : (
          Array.from(locationHoldingsMap.keys())
            .sort((a, b) => a.localeCompare(b))
            .map((key, index) => {
              const locationHoldings = locationHoldingsMap.get(key);
              const disabled = !allowOnShelfHolds && pickupLocationId === key;

              return (
                <React.Fragment key={key}>
                  <ListSubheader
                    disableGutters
                    aria-hidden="true"
                    component={"div"}
                  >
                    {key}
                  </ListSubheader>
                  {locationHoldings.map((hi, i) => {
                    const labelId = `checkbox-list-label-${hi.barcode}`;
                    return (
                      <ListItem
                        component={"div"}
                        key={hi.barcode}
                        disablePadding
                        disableGutters
                      >
                        <FormControlLabel
                          control={
                            <Checkbox
                              disabled={disabled}
                              checked={value.includes(hi.barcode)}
                              value={hi.barcode}
                              sx={{
                                color: error
                                  ? theme.palette.error.main
                                  : undefined,
                              }}
                              slotProps={{
                                input: {
                                  "aria-invalid": !!error,
                                  "aria-describedby":
                                    "place-hold-copies-helper-text",
                                  ref:
                                    index === 0 && i === 0
                                      ? setFirstCheckboxRef
                                      : undefined,
                                },
                              }}
                              onChange={(_, nextChecked) => {
                                onChange(
                                  nextChecked
                                    ? [...value, hi.barcode]
                                    : value.filter((v) => v !== hi.barcode),
                                );
                              }}
                            />
                          }
                          label={
                            <ListItemText
                              id={labelId}
                              primary={<CopyPrimaryDisplay holding={hi} />}
                              secondary={
                                <CopyStatusDisplay
                                  availability={
                                    itemAvailabilityReady &&
                                    itemAvailability.get(hi.barcode)
                                  }
                                />
                              }
                            />
                          }
                        />
                      </ListItem>
                    );
                  })}
                </React.Fragment>
              );
            })
        )}
      </List>
    </FormControl>
  );
});

interface PlaceHoldFormInput {
  requestType: "title" | "item";
  pickupLocation: string;
  barcodes: string[];
}

interface PlaceHoldDialogProps {
  resource: ResourceDWR;
  pickupLocationId: string;
  open: boolean;
  onClose: () => void;
  onSubmit: (payload: PlaceHoldPayloadItem[]) => void;
}
export const PlaceHoldDialog = ({
  open,
  resource,
  pickupLocationId,
  onClose,
  onSubmit: submitHandler,
}: PlaceHoldDialogProps) => {
  const { translate } = useLanguage();
  const { branchLimits } = useBranchLimits();
  const isMobile = useIsMobile();
  const { locationConfiguration } = useLocationConfiguration();
  const { userActionSettings } = locationConfiguration?.pacSettings || {};
  const { allowItemLevelHolds, allowTitleLevelHolds } =
    userActionSettings || {};
  const { singlePickupBranchId } = useSingleBranch();

  const defaultRequestType =
    (!allowTitleLevelHolds || resource?.serial) && allowItemLevelHolds
      ? "item"
      : "title";

  const formMethods = useForm<PlaceHoldFormInput>({
    mode: "onBlur",
    defaultValues: {
      requestType: defaultRequestType,
      pickupLocation: pickupLocationId,
      barcodes: [],
    },
  });
  const { handleSubmit, control, setValue, reset } = formMethods;
  const type = useWatch({
    name: "requestType",
    control: control,
  });
  const pickupLocation = useWatch({
    name: "pickupLocation",
    control: control,
  });
  const onShelfError = useOnShelfError(resource, pickupLocation);

  useEffect(() => {
    setValue("barcodes", []);
  }, [pickupLocation]);

  useEffect(() => {
    reset();
    if (pickupLocationId) {
      setValue("pickupLocation", pickupLocationId);
    }
    setValue("requestType", defaultRequestType);
  }, [open]);

  const onSubmit = async ({
    requestType,
    pickupLocation,
    barcodes,
  }: PlaceHoldFormInput) => {
    const payload: PlaceHoldPayloadItem[] =
      requestType === "title"
        ? [
            {
              pickupBranchId: pickupLocation,
              bibliographicId: resource.hostBibliographicId,
              titleLevelHold: true,
              serialItem: resource.serial,
              downloadable: resource.downloadable,
              interfaceType: InterfaceType.PAC,
            },
          ]
        : barcodes.map((barcode) => ({
            pickupBranchId: pickupLocation,
            bibliographicId: barcode,
            titleLevelHold: false,
            serialItem: resource.serial, // FIXME check item.serialItem
            downloadable: resource.downloadable,
            interfaceType: InterfaceType.PAC,
          }));

    submitHandler(payload);
  };

  const getPickupLocationSelector = () => {
    if (singlePickupBranchId) {
      return (
        <Typography>
          {translate("pickupLocation")}{" "}
          <strong>
            {branchLimits.find((bl) => bl.identifier === singlePickupBranchId)
              ?.displayName || `${singlePickupBranchId}`}
          </strong>
        </Typography>
      );
    }

    return (
      <Controller
        name={"pickupLocation"}
        control={control}
        rules={{
          required: translate("isRequired", {
            LABEL: translate("pickupLocation"),
          }),
        }}
        render={({
          field: { ref, onChange, ...field },
          fieldState: { error },
        }) => (
          <Autocomplete
            sx={{ maxWidth: { sm: "320px" } }}
            {...field}
            onChange={(_, value) => onChange(value)}
            renderInput={(params) => (
              <TextField
                {...params}
                required
                error={!!error}
                helperText={error ? <>{error.message}</> : " "}
                label={translate("pickupLocation")}
                inputRef={ref}
              />
            )}
            options={(branchLimits || [])
              .filter((bl) => !bl.hidden && bl.displayInPickupLocation)
              .map((bl) => bl.identifier)}
            getOptionLabel={(option) =>
              branchLimits.find((bl) => bl.identifier === option)
                ?.displayName || `${option}`
            }
          ></Autocomplete>
        )}
      />
    );
  };

  const requestTypeSelector = useMemo(() => {
    const showTitleLevel = allowTitleLevelHolds && !resource?.serial;
    if (allowItemLevelHolds && showTitleLevel) {
      return (
        <FormControl>
          <FormLabel id="requestTypeLabel">{translate("selectOne")}</FormLabel>
          <Controller<PlaceHoldFormInput>
            name="requestType"
            control={control}
            render={({ field }) => (
              <RadioGroup
                {...field}
                aria-labelledby="requestTypeLabel"
                name="requestType"
              >
                <FormControlLabel
                  value="title"
                  control={<Radio />}
                  label={translate("titleLevelOption")}
                />
                <FormControlLabel
                  value="item"
                  control={<Radio />}
                  label={translate("itemLevelOption")}
                />
              </RadioGroup>
            )}
          />
        </FormControl>
      );
    }
    return null;
  }, [
    allowTitleLevelHolds,
    allowItemLevelHolds,
    resource?.serial,
    translate,
    control,
  ]);

  if (!resource) {
    return null;
  }

  return (
    <FormDialog
      maxWidth="sm"
      formId="place-hold-dialog"
      open={open}
      onClose={onClose}
      headline={`${translate("placeARequestPrefix")} ${translate("request")}`}
      confirmButtonText={`${translate("submit")} ${translate("request")}`}
      dismissButtonText={`${translate("cancel")}`}
      submitButtonDisabled={onShelfError}
    >
      <form
        id="place-hold-dialog"
        noValidate
        autoComplete="off"
        onSubmit={handleSubmit(onSubmit)}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: 2,
          }}
        >
          <Typography component="h3" variant="h6">
            {resource.shortTitle}
          </Typography>
          {getPickupLocationSelector()}
          {onShelfError && (
            <Alert severity={"error"}>{translate("OnShelfError")}</Alert>
          )}
          {!onShelfError && requestTypeSelector}

          {/* RAIL-950: if you remove unmountOnExit, you must pass type as a prop to PlaceHoldCopies and conditionally render or conditionally require */}
          <Collapse unmountOnExit in={type === "item" && !onShelfError}>
            <PlaceHoldCopies
              resource={resource}
              control={control}
              pickupLocationId={pickupLocation}
            />
          </Collapse>
        </Box>
      </form>
    </FormDialog>
  );
};
